<template>
    <Dialog :visible="modelValue" @update:visible="$emit('update:modelValue', $event)"
        modal :showHeader="false"
        :style="{ width: 'min(620px, 95vw)', borderRadius: '16px', overflow: 'hidden', padding: '0' }"
        class="p-fluid" :closable="!loading">

        <!-- Header -->
        <div class="dialog-header">
            <div class="dialog-header-left">
                <div :class="['dialog-icon-wrap', iconWrapClass]">
                    <i :class="['pi pi-lock dialog-lock-icon', iconColorClass]"></i>
                </div>
                <div>
                    <p class="dialog-title">Billing Details</p>
                    <a
                        class="dialog-subtitle dialog-subtitle-link"
                        href="https://www.payhere.lk/"
                        target="_blank"
                        rel="noopener noreferrer"
                        >
                        Secured by PayHere
                    </a>
                </div>
            </div>
        </div>

        <!-- Body -->
        <div class="dialog-body">

            <!-- Free plan banner (onboarding) -->
            <div v-if="context === 'free_tokenize'" class="info-banner">
                <i class="pi pi-shield info-banner-icon"></i>
                <div>
                    <p class="info-banner-heading">Card verification — free plan</p>
                    <p class="info-banner-body">
                        To activate your free plan, PayHere places a <strong>LKR 30.00</strong> intent hold to verify your card.
                        This is <strong>not a charge</strong> — it is reversed immediately, and the refund reaches your card within 1 business day.
                        Your card is saved for seamless future upgrades.
                    </p>
                </div>
            </div>

            <!-- Add new card (standalone, no existing card) -->
            <div v-else-if="context === 'add_card'" class="info-banner">
                <i class="pi pi-shield info-banner-icon"></i>
                <div>
                    <p class="info-banner-heading">Card verification — LKR 30.00 intent hold</p>
                    <p class="info-banner-body">
                        PayHere places a <strong>LKR 30.00</strong> confirmation hold to verify your card.
                        This is <strong>not a charge</strong> — it is reversed immediately and the refund reaches your card
                        within <strong>1 business day</strong>. Your card will be saved for automatic billing.
                    </p>
                </div>
            </div>

            <!-- Replace existing card (standalone) -->
            <div v-else-if="context === 'replace_card'" class="replace-banner">
                <i class="pi pi-sync replace-banner-icon"></i>
                <div>
                    <p class="replace-banner-heading">Replacing your saved card</p>
                    <p class="replace-banner-body">
                        <template v-if="existingCard?.cardNo">
                            Your current card <strong>({{ existingCard.cardNo }})</strong> will be
                        </template>
                        <template v-else>Your current saved card will be</template>
                        <strong> deactivated</strong> from system access once your new card is approved.
                        A <strong>LKR 30.00</strong> confirmation hold is placed on your new card to verify it —
                        this is <strong>not a charge</strong> and is refunded within 1 business day.
                    </p>
                </div>
            </div>

            <!-- Paid plan banner -->
            <div v-else class="warning-banner">
                <i class="pi pi-info-circle warning-banner-icon"></i>
                <div>
                    <p class="warning-banner-heading">Complete your subscription payment</p>
                    <p class="warning-banner-body">
                        Enter your billing details to activate your
                        <strong>{{ selectedPackage?.name }}</strong> plan.
                        <template v-if="form.selectedGrade">
                            You'll be charged
                            <template v-if="appliedDiscount?.isFree"><strong>FREE this month</strong></template>
                            <template v-else><strong>LKR {{ appliedDiscount ? appliedDiscount.discountedAmount : form.selectedGrade.amount }}.00/month</strong></template>
                            <template v-if="appliedDiscount && !appliedDiscount.isFree">
                                <span class="saved-inline"> (saved LKR {{ appliedDiscount.discountSavedAmount }})</span>
                            </template>
                            and your card saved for future billing.
                        </template>
                        <template v-else-if="gradePricing?.length">
                            The charge amount depends on your grade selection below.
                        </template>
                        <template v-else>
                            You'll be charged
                            <template v-if="appliedDiscount?.isFree"><strong>FREE this month</strong></template>
                            <template v-else><strong>LKR {{ appliedDiscount ? appliedDiscount.discountedAmount : selectedPackage?.priceLabel }}.00/month</strong></template>
                            <template v-if="appliedDiscount && !appliedDiscount.isFree">
                                <span class="saved-inline"> (saved LKR {{ appliedDiscount.discountSavedAmount }})</span>
                            </template>
                            and your card saved for future billing.
                        </template>
                    </p>
                </div>
            </div>

            <!-- Form -->
            <div class="grid formgrid form-grid">
                <div v-for="field in formFields" :key="field.key"
                    :class="['field', 'mb-2', field.fullWidth ? 'col-12' : 'col-12 md:col-6', 'form-col']">

                    <label class="form-label">
                        {{ field.label }}
                        <span v-if="field.required" class="form-label-required">*</span>
                    </label>

                    <!-- Grade pricing dropdown -->
                    <Select v-if="field.type === 'gradeSelect'"
                        v-model="form.selectedGrade"
                        :options="gradePricing"
                        optionLabel="label"
                        :placeholder="field.placeholder"
                        :class="['form-select', { 'p-invalid': submitted && !form.selectedGrade }]"
                        :style="submitted && !form.selectedGrade
                            ? { border: '1px solid #EF4444' }
                            : { border: '1px solid #D1D5DB' }"
                    >
                        <template #option="{ option }">
                            <div class="flex justify-content-between w-full">
                                <span>{{ option.label }}</span>
                                <span class="text-600 text-sm">Rs. {{ option.amount }}.00/mo</span>
                            </div>
                        </template>
                        <template #value="{ value }">
                            <span v-if="value">{{ value.label }} — Rs. {{ value.amount }}.00/mo</span>
                            <span v-else class="text-400">{{ field.placeholder }}</span>
                        </template>
                    </Select>

                    <!-- City dropdown -->
                    <Select v-else-if="field.type === 'select'"
                        v-model="form[field.key]"
                        :options="field.options"
                        optionLabel="name"
                        :placeholder="field.placeholder"
                        :class="['form-select', { 'p-invalid': submitted && field.required && !form[field.key] }]"
                        :style="submitted && field.required && !form[field.key]
                            ? { border: '1px solid #EF4444' }
                            : { border: '1px solid #D1D5DB' }"
                    />

                    <!-- Phone number field — digits only, max 10 -->
                    <InputText v-else-if="field.key === 'phone'"
                        v-model="form.phone"
                        :placeholder="field.placeholder"
                        inputmode="numeric"
                        :class="['form-input', { 'p-invalid': submitted && field.required && !form.phone }]"
                        :style="submitted && field.required && !form.phone
                            ? { border: '1px solid #EF4444' }
                            : { border: '1px solid #D1D5DB' }"
                        @keydown="onPhoneKeydown"
                        @paste="onPhonePaste"
                    />

                    <!-- Editable text field -->
                    <InputText v-else-if="!field.disabled && !field.type"
                        v-model="form[field.key]"
                        :placeholder="field.placeholder"
                        :class="['form-input', { 'p-invalid': submitted && field.required && !form[field.key] }]"
                        :style="submitted && field.required && !form[field.key]
                            ? { border: '1px solid #EF4444' }
                            : { border: '1px solid #D1D5DB' }"
                    />

                    <!-- Disabled / read-only field -->
                    <div v-else class="form-readonly">
                        <span class="form-readonly-value">{{ form[field.key] }}</span>
                        <i class="pi pi-lock form-readonly-icon"></i>
                    </div>

                    <small v-if="field.key === 'phone' && phoneError" class="form-error">
                        {{ phoneError }}
                    </small>
                </div>
            </div>

            <!-- Promo code — paid subscribe only, and only when caller hasn't handled it already -->
            <div v-if="context === 'paid_subscribe' && allowPromo" class="promo-section">
                <div class="promo-divider"></div>

                <!-- Input state -->
                <div v-if="!appliedDiscount" class="promo-field">
                    <label class="form-label">PROMO CODE <span class="promo-optional">(Optional)</span></label>
                    <div class="promo-input-row">
                        <InputText
                            v-model="promoCode"
                            placeholder="Enter promo code"
                            class="promo-text-input"
                            :disabled="isApplying"
                            @keyup.enter="applyPromoCode"
                            @input="promoError = null; showPromoWarning = false"
                        />
                        <Button
                            label="Apply"
                            :class="promoApplyBtnClass"
                            :loading="isApplying"
                            :disabled="isApplying || !promoCode.trim()"
                            @click="applyPromoCode"
                        />
                    </div>
                    <!-- Reserved message space — fixed height, no layout shift -->
                    <div class="promo-msg-area">
                        <small v-if="promoError" class="promo-error-msg">{{ promoError }}</small>
                        <div v-else-if="showPromoWarning" class="promo-warning-msg">
                            <i class="pi pi-exclamation-triangle promo-warn-icon"></i>
                            <span>Apply your code first, or
                                <span class="promo-action-link" @click="removeDiscount">remove it</span>
                                /
                                <span class="promo-action-link" @click="continueWithoutCode">continue without code</span>.
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Applied state — same vertical space -->
                <div v-else>
                    <div class="promo-applied-banner">
                        <div class="promo-applied-left">
                            <i class="pi pi-check-circle promo-applied-icon"></i>
                            <div class="promo-applied-text">
                                <span class="promo-applied-title">
                                    {{ appliedDiscount.isFree ? 'Code applied — This month is FREE!' : 'Promo code applied!' }}
                                </span>
                                <span v-if="!appliedDiscount.isFree" class="promo-applied-saving">You saved LKR {{ appliedDiscount.discountSavedAmount }}</span>
                            </div>
                        </div>
                        <span class="promo-remove-link" @click="removeDiscount">Remove</span>
                    </div>
                    <div v-if="appliedDiscount.isFree && !existingCard" class="free-notice">
                        <i class="pi pi-exclamation-triangle free-notice-icon"></i>
                        <span>A refundable LKR 30 card verification is required to save your card for future monthly renewals.</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Footer -->
        <template #footer>
            <div class="footer-wrap">

                <!-- Payment disclaimer -->
                <div class="payment-disclaimer">
                    <p :class="['payment-disclaimer-heading', disclaimerHeadingClass]">Disclaimer</p>
                    <p class="payment-disclaimer-body">
                        Payments are securely processed through Payhere. Applicable payment processing fees
                        charged by the payment gateway are detected separately from the transaction amount.
                    </p>
                </div>

                <!-- Mobile -->
                <div class="flex flex-column md:hidden gap-2 w-full">
                    <Button
                        :label="isCardVerifyContext ? 'Continue to Card Verification' : (appliedDiscount?.isFree ? 'Verify Card & Subscribe' : 'Continue to Payment')"
                        icon="pi pi-lock" :loading="loading" :disabled="loading || isApplying" @click="handleSubmit"
                        :class="[primaryBtnClass, 'w-full']"
                    />
                    <Button label="Cancel"
                        class="btn-secondary w-full"
                        @click="$emit('update:modelValue', false)" :disabled="loading || isApplying"
                    />
                </div>
                <!-- Desktop -->
                <div class="hidden md:flex align-items-center justify-content-between w-full footer-desktop">
                    <div class="footer-card-info">
                        <i class="pi pi-credit-card footer-card-icon"></i>
                        <span class="footer-card-label">Visa · Mastercard accepted</span>
                    </div>
                    <div class="footer-actions">
                        <Button label="Cancel"
                            class="btn-secondary"
                            @click="$emit('update:modelValue', false)" :disabled="loading || isApplying" />
                        <Button
                            :label="isCardVerifyContext ? 'Continue to Card Verification' : (appliedDiscount?.isFree ? 'Verify Card & Subscribe' : 'Continue to Payment')"
                            icon="pi pi-lock" :loading="loading" :disabled="loading || isApplying" @click="handleSubmit"
                            :class="primaryBtnClass"
                        />
                    </div>
                </div>

            </div>
        </template>
    </Dialog>
</template>

<script setup>
import { reactive, ref, watch, computed, defineProps, defineEmits } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import { cities } from '@/utils/cities';
import { useAuthStore } from '@/stores/authStore';
import { backendApi } from '@/api/axios';
import { DISCOUNT_ENDPOINTS } from '@/api/endpoints';

const authStore = useAuthStore();

const primaryBtnClass = computed(() => {
    const role = authStore.userType;
    if (role === 'tutor')     return 'btn-primary btn-primary--tutor';
    if (role === 'institute') return 'btn-primary btn-primary--institute';
    return 'btn-primary btn-primary--student';
});

const iconWrapClass = computed(() => {
    const role = authStore.userType;
    if (role === 'tutor')     return 'dialog-icon-wrap--tutor';
    if (role === 'institute') return 'dialog-icon-wrap--institute';
    return 'dialog-icon-wrap--student';
});

const iconColorClass = computed(() => {
    const role = authStore.userType;
    if (role === 'tutor')     return 'dialog-lock-icon--tutor';
    if (role === 'institute') return 'dialog-lock-icon--institute';
    return 'dialog-lock-icon--student';
});

const disclaimerHeadingClass = computed(() => {
    const role = authStore.userType;
    if (role === 'tutor')     return 'payment-disclaimer-heading--tutor';
    if (role === 'institute') return 'payment-disclaimer-heading--institute';
    return 'payment-disclaimer-heading--student';
});

const promoApplyBtnClass = computed(() => {
    const role = authStore.userType;
    if (role === 'tutor')     return 'promo-apply-btn promo-apply-btn--tutor';
    if (role === 'institute') return 'promo-apply-btn promo-apply-btn--institute';
    return 'promo-apply-btn promo-apply-btn--student';
});

const isCardVerifyContext = computed(() =>
    ['free_tokenize', 'add_card', 'replace_card'].includes(props.context)
);

const props = defineProps({
    modelValue:      { type: Boolean, required: true },
    context:         { type: String, default: 'free_tokenize' },
    selectedPackage: { type: Object, default: null },
    loading:         { type: Boolean, default: false },
    initialValues:   { type: Object, default: () => ({}) },
    gradePricing:      { type: Array,   default: null },
    preselectedGrade:  { type: Object,  default: null },
    existingCard:      { type: Object,  default: null },
    allowPromo:        { type: Boolean, default: true },
});

const emit = defineEmits(['update:modelValue', 'submit']);

const baseFields = [
    { key: 'firstName', label: 'First Name',     placeholder: 'First Name',       required: true },
    { key: 'lastName',  label: 'Last Name',      placeholder: 'Last Name',        required: true },
    { key: 'email',     label: 'Email Address',                                   disabled: true },
    { key: 'phone',     label: 'Phone Number',   placeholder: '07XXXXXXXX',       required: true },
    { key: 'address',   label: 'Street Address', placeholder: 'Street Address',   required: true },
    { key: 'city',      label: 'City',           placeholder: 'Select your city', required: true, type: 'select', options: cities },
    { key: 'country',   label: 'Country',                                         disabled: true, fullWidth: true },
];

const formFields = computed(() => {
    if (props.gradePricing?.length) {
        return [
            { key: 'selectedGrade', label: 'Select Your Grade', placeholder: 'Select your grade', required: true, fullWidth: true, type: 'gradeSelect' },
            ...baseFields,
        ];
    }
    return baseFields;
});

const form = reactive({
    firstName: '', lastName: '', email: '',
    phone: '', address: '', city: null, country: 'Sri Lanka',
    selectedGrade: null,
});

const submitted  = ref(false);
const phoneError = ref('');

function onPhoneKeydown(e) {
    const nav = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
    if (nav.includes(e.key)) return;
    if (!/^\d$/.test(e.key)) { e.preventDefault(); return; }
    if (form.phone.length >= 10) e.preventDefault();
}

function onPhonePaste(e) {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text');
    form.phone = text.replace(/\D/g, '').slice(0, 10);
}

// Promo code state
const promoCode         = ref('');
const isApplying        = ref(false);
const appliedDiscount   = ref(null);
const promoError        = ref(null);
const showPromoWarning  = ref(false);

const subscriptionPrice = computed(() => {
    if (form.selectedGrade?.amount) return parseFloat(form.selectedGrade.amount);
    return parseFloat(props.selectedPackage?.priceLabel) || 0;
});

function resetPromo() {
    promoCode.value        = '';
    isApplying.value       = false;
    appliedDiscount.value  = null;
    promoError.value       = null;
    showPromoWarning.value = false;
}

async function applyPromoCode() {
    if (!promoCode.value.trim()) return;
    if (props.gradePricing?.length && !form.selectedGrade) {
        promoError.value = 'Please select a grade first.';
        return;
    }
    promoError.value       = null;
    showPromoWarning.value = false;
    isApplying.value       = true;
    try {
        const res  = await backendApi.post(DISCOUNT_ENDPOINTS.APPLY, {
            code: promoCode.value.trim(),
            subscriptionPrice: subscriptionPrice.value,
        });
        const data = res.data?.data ?? res.data;
        appliedDiscount.value = {
            discountId:          data.discountId,
            discountedAmount:    parseFloat(data.amountToPay),
            discountSavedAmount: parseFloat(data.savedAmount),
            isFree:              data.amountToPay === '0.00',
        };
    } catch (err) {
        promoError.value = err?.response?.data?.message || 'Invalid promo code. Please try again.';
    } finally {
        isApplying.value = false;
    }
}

function removeDiscount() {
    resetPromo();
}

function continueWithoutCode() {
    promoCode.value        = '';
    promoError.value       = null;
    showPromoWarning.value = false;
    doSubmit();
}

// Sync form with initialValues whenever they change or dialog opens
watch(() => props.initialValues, (vals) => {
    if (!vals) return;
    form.firstName = vals.firstName || '';
    form.lastName  = vals.lastName  || '';
    form.email     = vals.email     || '';
    form.phone     = vals.phone     || '';
    form.address   = vals.address   || '';
    form.city      = cities.find(c => c.code === vals.city) || null;
}, { immediate: true, deep: true });

// Reset validation + promo when dialog opens
watch(() => props.modelValue, (open) => {
    if (open) {
        submitted.value    = false;
        phoneError.value   = '';
        form.selectedGrade = props.preselectedGrade || null;
        resetPromo();
    }
});

// If grade changes after a discount was applied, the discount amount is no longer valid
watch(() => form.selectedGrade, (newGrade, oldGrade) => {
    if (oldGrade && newGrade?.gradeKey !== oldGrade?.gradeKey && appliedDiscount.value) {
        resetPromo();
    }
});

function handleSubmit() {
    if (promoCode.value.trim() && !appliedDiscount.value) {
        promoError.value       = null;
        showPromoWarning.value = true;
        return;
    }
    doSubmit();
}

function doSubmit() {
    showPromoWarning.value = false;
    submitted.value        = true;
    phoneError.value       = '';

    const requiredFields = formFields.value.filter(f => f.required).map(f => f.key);
    const allFilled = requiredFields.every(k => {
        const val = form[k];
        if (val === null || val === undefined || val === '') return false;
        if (typeof val === 'object') return true;
        return val.toString().trim().length > 0;
    });
    if (!allFilled) return;

    const phoneRegex = /^(?:\+94|0)?7\d{8}$/;
    if (!phoneRegex.test(form.phone)) {
        phoneError.value = 'Please enter a valid mobile number (e.g. 07XXXXXXXX).';
        return;
    }

    emit('submit', {
        ...form,
        city:             form.city?.code || '',
        selectedGradeKey: form.selectedGrade?.gradeKey || null,
        ...(appliedDiscount.value ? {
            discountId:          appliedDiscount.value.discountId,
            discountedAmount:    appliedDiscount.value.discountedAmount,
            discountSavedAmount: appliedDiscount.value.discountSavedAmount,
        } : {}),
    });
}
</script>

<style scoped>

.dialog-header {
    padding: 1.1rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.dialog-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
}

.dialog-icon-wrap {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.dialog-icon-wrap--tutor     { background: #DCFCE7; }
.dialog-icon-wrap--student   { background: #FEE2E2; }
.dialog-icon-wrap--institute { background: #FEF9C3; }

.dialog-lock-icon { font-size: 15px; }

.dialog-lock-icon--tutor     { color: #25c560; }
.dialog-lock-icon--student   { color: #ec0e37; }
.dialog-lock-icon--institute { color: #CA8A04; }

.dialog-title {
    font-size: 15px;
    font-weight: 600;
    margin: 0;
    color: #111827;
}

.dialog-subtitle {
    font-size: 14px;
    color: #6B7280;
    margin: 0;
}

.dialog-subtitle-link {
    color: #6B7280;
    text-decoration: none;
}

.dialog-subtitle-link:hover {
    color: #2563EB;
    text-decoration: underline;
}

.dialog-body {
    padding: 1.25rem 1.5rem;
}

.info-banner {
    border: 1px solid #BFDBFE;
    background: #EFF6FF;
    border-radius: 8px;
    padding: 0.875rem 1rem;
    margin-bottom: 1.25rem;
    display: flex;
    gap: 10px;
    align-items: flex-start;
}

.info-banner-icon {
    color: #1D4ED8;
    font-size: 15px;
    margin-top: 1px;
    flex-shrink: 0;
}

.info-banner-heading {
    font-size: 13px;
    font-weight: 600;
    margin: 0 0 3px;
    color: #1E40AF;
}

.info-banner-body {
    font-size: 12px;
    margin: 0;
    color: #1D4ED8;
    line-height: 1.65;
}

.replace-banner {
    border: 1px solid #FED7AA;
    background: #FFF7ED;
    border-radius: 8px;
    padding: 0.875rem 1rem;
    margin-bottom: 1.25rem;
    display: flex;
    gap: 10px;
    align-items: flex-start;
}

.replace-banner-icon {
    color: #C2410C;
    font-size: 15px;
    margin-top: 1px;
    flex-shrink: 0;
}

.replace-banner-heading {
    font-size: 13px;
    font-weight: 600;
    margin: 0 0 3px;
    color: #9A3412;
}

.replace-banner-body {
    font-size: 12px;
    margin: 0;
    color: #C2410C;
    line-height: 1.65;
}

.warning-banner {
    border: 1px solid #FEF08A;
    background: #FEFCE8;
    border-radius: 8px;
    padding: 0.875rem 1rem;
    margin-bottom: 1.25rem;
    display: flex;
    gap: 10px;
    align-items: flex-start;
}

.warning-banner-icon {
    color: #CA8A04;
    font-size: 15px;
    margin-top: 1px;
    flex-shrink: 0;
}

.warning-banner-heading {
    font-size: 13px;
    font-weight: 600;
    margin: 0 0 3px;
    color: #A16207;
}

.warning-banner-body {
    font-size: 12px;
    margin: 0;
    color: #CA8A04;
    line-height: 1.65;
}

.saved-inline {
    color: #15803d;
    font-weight: 600;
}

.form-grid {
    margin: 0 -6px;
}

.form-col {
    padding: 0 6px;
}

.form-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    color: #6B7280;
    display: block;
    margin-bottom: 5px;
    text-transform: uppercase;
}

.form-label-required {
    color: #EF4444;
    margin-left: 2px;
}

.form-select {
    width: 100%;
    font-size: 14px;
    border-radius: 8px;
}

.form-input {
    width: 100%;
    font-size: 14px;
    padding: 8px 11px;
    border-radius: 8px;
    outline: none;
    box-sizing: border-box;
    background: #fff;
    color: #111827;
}

.form-readonly {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 11px;
    border-radius: 8px;
    border: 1px solid #E5E7EB;
    background: #F9FAFB;
}

.form-readonly-value {
    font-size: 14px;
    color: #6B7280;
}

.form-readonly-icon {
    margin-left: auto;
    font-size: 12px;
    color: #9CA3AF;
}

.form-error {
    font-size: 11px;
    color: #EF4444;
    display: block;
    margin-top: 4px;
}

/* Promo code section */
.promo-section {
    margin-top: 0.75rem;
}

.promo-divider {
    height: 1px;
    background: #E5E7EB;
    margin-bottom: 0.75rem;
}

.promo-optional {
    font-size: 10px;
    font-weight: 400;
    color: #9CA3AF;
    text-transform: none;
    letter-spacing: 0;
}

.promo-field {
    display: flex;
    flex-direction: column;
}

.promo-input-row {
    display: flex;
    gap: 8px;
    align-items: center;
}

.promo-text-input {
    flex: 1;
    font-size: 13px;
    height: 34px;
    padding: 6px 10px;
    border-radius: 7px;
    border: 1px solid #D1D5DB;
    outline: none;
    background: #fff;
    color: #111827;
    box-sizing: border-box;
}

.promo-apply-btn {
    color: #fff !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    height: 34px;
    padding: 0 14px !important;
    border-radius: 7px !important;
    white-space: nowrap;
}

.promo-apply-btn--tutor {
    background-color: #13be85 !important;
    border: 1px solid #13be85 !important;
}
.promo-apply-btn--tutor:hover:not(:disabled) {
    background-color: #0fa872 !important;
    border-color: #0fa872 !important;
}

.promo-apply-btn--student {
    background-color: #ec0e37 !important;
    border: 1px solid #ec0e37 !important;
}
.promo-apply-btn--student:hover:not(:disabled) {
    background-color: #c90b2f !important;
    border-color: #c90b2f !important;
}

.promo-apply-btn--institute {
    background-color: #e9a30c !important;
    border: 1px solid #e9a30c !important;
}
.promo-apply-btn--institute:hover:not(:disabled) {
    background-color: #cc8f0a !important;
    border-color: #cc8f0a !important;
}

/* Fixed-height message area — prevents layout shift */
.promo-msg-area {
    min-height: 36px;
    padding-top: 4px;
    display: flex;
    align-items: flex-start;
}

.promo-error-msg {
    font-size: 11px;
    color: #EF4444;
}

.promo-warning-msg {
    display: flex;
    align-items: flex-start;
    gap: 5px;
    font-size: 11px;
    color: #92400E;
    background: #FFFBEB;
    border: 1px solid #FDE68A;
    border-radius: 6px;
    padding: 5px 8px;
    line-height: 1.5;
    width: 100%;
}

.promo-warn-icon {
    font-size: 10px;
    margin-top: 2px;
    flex-shrink: 0;
    color: #D97706;
}

.promo-action-link {
    color: #1D4ED8;
    cursor: pointer;
    text-decoration: underline;
    white-space: nowrap;
}

.promo-action-link:hover {
    color: #1E40AF;
}

.promo-applied-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    background: #F0FDF4;
    border: 1px solid #BBF7D0;
    border-radius: 8px;
}

.promo-applied-left {
    display: flex;
    align-items: center;
    gap: 8px;
}

.promo-applied-icon {
    color: #16A34A;
    font-size: 14px;
    flex-shrink: 0;
}

.promo-applied-text {
    display: flex;
    flex-direction: column;
    gap: 1px;
}

.promo-applied-title {
    font-size: 12px;
    font-weight: 600;
    color: #15803D;
}

.promo-applied-saving {
    font-size: 11px;
    color: #16A34A;
}

.promo-remove-link {
    font-size: 11px;
    color: #9CA3AF;
    cursor: pointer;
    text-decoration: underline;
    white-space: nowrap;
    margin-left: 8px;
}

.promo-remove-link:hover {
    color: #6B7280;
}

.free-notice {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: 11px;
    color: #92400E;
    background: #FFFBEB;
    border: 1px solid #FDE68A;
    border-radius: 6px;
    padding: 6px 10px;
    line-height: 1.5;
    margin-top: 8px;
}

.free-notice-icon {
    font-size: 10px;
    margin-top: 2px;
    flex-shrink: 0;
    color: #D97706;
}

/* Payment disclaimer */
.footer-wrap {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.payment-disclaimer {
    text-align: center;
    border-top: 1px solid #E5E7EB;
    padding-top: 0.75rem;
    margin-bottom: 0.85rem;
}

.payment-disclaimer-heading {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin: 0 0 4px;
}

.payment-disclaimer-heading--tutor     { color: #13be85; }
.payment-disclaimer-heading--student   { color: #ec0e37; }
.payment-disclaimer-heading--institute { color: #CA8A04; }

.payment-disclaimer-body {
    font-size: 11px;
    line-height: 1.6;
    color: #6B7280;
    margin: 0 auto;
    max-width: 500px;
}

/* Buttons */
.btn-primary {
    font-size: 13px;
    font-weight: 600;
    padding: 8px 18px;
    border-radius: 8px;
    border: none !important;
    color: #fff !important;
}

.btn-primary--tutor {
    background: #13be85 !important;
}
.btn-primary--tutor:hover:not(:disabled) {
    background: #0fa872 !important;
}

.btn-primary--student {
    background: #ec0e37 !important;
}
.btn-primary--student:hover:not(:disabled) {
    background: #c90b2f !important;
}

.btn-primary--institute {
    background: #e9a30c !important;
}
.btn-primary--institute:hover:not(:disabled) {
    background: #cc8f0a !important;
}

.btn-secondary {
    font-size: 13px;
    padding: 8px 16px;
    border-radius: 8px;
    background: transparent !important;
    border: 1px solid #D1D5DB !important;
    color: #374151 !important;
}

.footer-desktop {
    padding: 0.25rem 0;
}

.footer-card-info {
    display: flex;
    align-items: center;
    gap: 6px;
}

.footer-card-icon {
    font-size: 13px;
    color: #9CA3AF;
}

.footer-card-label {
    font-size: 11px;
    color: #9CA3AF;
}

.footer-actions {
    display: flex;
    gap: 8px;
}

</style>
